/**
 * TNK-126 phase 1 — operator-side paginated cards feed hook.
 *
 * Mirrors `useStorefrontFeed` from TNK-124 — same args shape, same
 * three-state phase, same fetchToken race guard. Phase 1 ships the
 * plumbing so the inventory grid (`/inventory`, `/cards`, etc.) can
 * adopt it without each one rebuilding the hook layer.
 *
 * Phase 2 (separate ticket) does the actual `/inventory` restructure,
 * the AppContext lazy-hydration migration, and the long-tail consumer
 * audit. This hook is the foundation those phases build on.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { Card } from "@/context/AppContext";
import {
  fetchCardsFeed,
  type CardsFeedFilters,
  type CardsFeedSort,
} from "@/context/inventorySync";

export type CardsFeedPhase =
  | { phase: "loading" }
  | {
      phase: "ready";
      cards: Card[];
      hasMore: boolean;
      /** Total rows matching the active filter set (not just the loaded
       *  page). Drives the page header's "X of N matching" denominator. */
      total: number;
    }
  | { phase: "error" };

export interface UseCardsFeedArgs {
  query: string;
  sort: CardsFeedSort;
  filters: CardsFeedFilters;
  /** Archive view — undefined for default active-only, `archived` for
   *  active+archived, `only-archived` for the Archived tab. */
  include?: "archived" | "only-archived";
}

export interface UseCardsFeedResult {
  state: CardsFeedPhase;
  /** Trigger fetch of the next page. No-op when already loading, when
   *  no further pages exist, or when an error is currently in effect. */
  loadMore: () => void;
  /** True iff the next page is currently in-flight. */
  loadingMore: boolean;
  /** True iff a fresh first page is in-flight after a filter / sort /
   *  query change. Lets the grid stay visible during the swap. */
  refetching: boolean;
}

const PAGE_SIZE = 60;

export function useCardsFeed(args: UseCardsFeedArgs): UseCardsFeedResult {
  const [state, setState] = useState<CardsFeedPhase>({ phase: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [refetching, setRefetching] = useState(false);

  // Args fingerprint — refire the first-page effect whenever any
  // semantic value changes, even when the parent passes a fresh object
  // reference with identical contents.
  const fingerprint = useMemo(() => JSON.stringify(args), [args]);

  // Latest args mirrored into a ref so loadMore picks up the current
  // filters without re-binding any sentinel observer the consumer wires.
  const argsRef = useRef(args);
  useEffect(() => {
    argsRef.current = args;
  }, [args]);

  // fetchToken increments on every fresh first-page fetch. The async
  // resolve closure captures its issuing token and discards its
  // result if a newer fetch has since started.
  const fetchToken = useRef(0);
  const cursorRef = useRef<string | null>(null);

  // First-page fetch. Re-runs whenever the fingerprint changes.
  useEffect(() => {
    const token = ++fetchToken.current;
    cursorRef.current = null;
    setRefetching(true);
    void (async () => {
      try {
        const page = await fetchCardsFeed({
          limit: PAGE_SIZE,
          q: args.query,
          sort: args.sort,
          filters: args.filters,
          include: args.include,
        });
        if (token !== fetchToken.current) return;
        cursorRef.current = page.nextCursor;
        setState({
          phase: "ready",
          cards: page.cards,
          hasMore: page.nextCursor !== null,
          total: page.total,
        });
        setRefetching(false);
      } catch {
        if (token !== fetchToken.current) return;
        setState({ phase: "error" });
        setRefetching(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    if (state.phase !== "ready") return;
    if (!state.hasMore) return;
    const cursor = cursorRef.current;
    if (!cursor) return;
    const token = fetchToken.current;
    setLoadingMore(true);
    void (async () => {
      try {
        const page = await fetchCardsFeed({
          limit: PAGE_SIZE,
          cursor,
          q: argsRef.current.query,
          sort: argsRef.current.sort,
          filters: argsRef.current.filters,
          include: argsRef.current.include,
        });
        if (token !== fetchToken.current) return;
        cursorRef.current = page.nextCursor;
        setState((prev) =>
          prev.phase === "ready"
            ? {
                phase: "ready",
                cards: [...prev.cards, ...page.cards],
                hasMore: page.nextCursor !== null,
                total: page.total,
              }
            : prev,
        );
      } catch {
        // Don't tear down the existing list on a paginate failure —
        // the operator's already-rendered cards stay readable. The
        // next loadMore attempt can retry.
      } finally {
        setLoadingMore(false);
      }
    })();
  }, [state, loadingMore]);

  return { state, loadMore, loadingMore, refetching };
}
